import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { avatarColor, makeInitials, TONE_OPTIONS } from '../../utils/helpers';
import { authApi } from '../../api/auth';

export default function Settings() {
  const { currentUser } = useApp();
  const [panel, setPanel] = useState('voice');
  const isAdmin = currentUser?.role === 'admin';

  const PANELS = [
    { id:'voice',    icon:'🎙', label:'Brand Voice',    section:'Content' },
    { id:'partners', icon:'🏨', label:'Partners',       section:'Content' },
    { id:'apikeys',  icon:'🔑', label:'API Keys',       section:'Content' },
    ...(isAdmin ? [
      { id:'users',    icon:'👥', label:'Users & Access', section:'Admin' },
      { id:'brand',    icon:'☕', label:'Brand Settings', section:'Admin' },
      { id:'activity', icon:'📋', label:'Activity Log',   section:'Admin' },
    ] : []),
    { id:'account', icon:'👤', label:'My Account', section:'Account' },
  ];

  const sections = [...new Set(PANELS.map(p => p.section))];

  const PANEL_COMPONENTS = {
    voice:    VoicePanel,
    partners: PartnersPanel,
    apikeys:  APIKeysPanel,
    users:    UsersPanel,
    brand:    BrandPanel,
    activity: ActivityPanel,
    account:  AccountPanel,
  };

  const ActivePanel = PANEL_COMPONENTS[panel] || (() => <div className="empty-state">Panel not found.</div>);

  return (
    <div style={{ display:'flex', height:'100%', minHeight:0 }}>
      <nav className="set-nav">
        {sections.map(section => (
          <div key={section}>
            <div className="set-nav-sect">{section}</div>
            {PANELS.filter(p => p.section === section).map(p => (
              <button key={p.id} className={`set-nav-item${panel === p.id ? ' active' : ''}`} onClick={() => setPanel(p.id)}>
                {p.icon}&nbsp; {p.label}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div style={{ flex:1, overflowY:'auto', padding:'24px 28px' }}>
        <ActivePanel onSwitchPanel={setPanel} />
      </div>
    </div>
  );
}

// ── Brand Voice ──────────────────────────────────────────────
function VoicePanel() {
  const { voiceState, updateVoice } = useApp();
  const v = voiceState || {};
  const [form, setForm] = useState({
    description:      v.description||'',
    pov:              v.pov||'',
    audience:         v.audience||'',
    proof_points:     v.proof_points||'',
    signature_phrase: v.signature_phrase||'',
    examples:         v.examples||'',
    tones:            v.tones||[],
    banned:           v.banned||[],
  });
  const [bannedInput, setBannedInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  const filledCount = [form.description,form.pov,form.audience,form.proof_points,form.signature_phrase,form.examples,form.tones.length>0,form.banned.length>0].filter(Boolean).length;

  function toggleTone(id) { setForm(f => ({ ...f, tones: f.tones.includes(id) ? f.tones.filter(t=>t!==id) : [...f.tones,id] })); }
  function addBanned() { const w=bannedInput.trim(); if(!w||form.banned.includes(w)) return; setForm(f=>({...f,banned:[...f.banned,w]})); setBannedInput(''); }
  function removeBanned(w) { setForm(f=>({...f,banned:f.banned.filter(b=>b!==w)})); }

  async function save() {
    setSaving(true);
    try {
      await updateVoice(form);
      setSaved(true); setTimeout(()=>setSaved(false),2500);
    } catch(err) { alert('Failed to save: '+err.message); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="set-title">Brand Voice</div>
      <div className="set-sub">Teach the AI how Tayst sounds — applied to every post automatically.</div>
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', borderRadius:9, background:filledCount>0?'#e4efe6':'#fdf3d8', marginBottom:16 }}>
        <span style={{ fontSize:14 }}>{filledCount>0?'✅':'⚠️'}</span>
        <span style={{ fontSize:12, fontWeight:600, color:filledCount>0?'#2d5c3c':'#b8962e' }}>
          {filledCount>0?`Voice active — ${filledCount}/8 fields complete`:'No voice profile — posts use default AI tone'}
        </span>
        <div style={{ display:'flex', gap:3, marginLeft:'auto' }}>
          {Array.from({length:8},(_,i)=><div key={i} style={{ width:8, height:8, borderRadius:'50%', background:i<filledCount?'#2d5c3c':'#e5e7eb' }} />)}
        </div>
      </div>
      {[['description','How you\'d describe Tayst in your own words','e.g. We bring genuinely good coffee to hotels that care about what they serve.'],
        ['pov','Your genuine POV','e.g. Most sustainable coffee is sustainable in name only.'],
        ['audience','Who you\'re writing for (LinkedIn)','e.g. Hotel GMs and F&B directors at boutique properties.'],
        ['proof_points','Key proof points & facts','e.g. BPI-certified compostable pods. RA certified. 500+ hotels.'],
        ['examples','Paste examples of your real writing','Paste emails, pitches, posts — the AI studies the rhythm.'],
      ].map(([field,label,placeholder]) => (
        <div className="f-row" key={field}>
          <div className="f-lbl">{label}</div>
          <textarea className="f-ta" rows={3} value={form[field]} placeholder={placeholder}
            onChange={e=>setForm(f=>({...f,[field]:e.target.value}))} />
        </div>
      ))}
      <div className="f-row">
        <div className="f-lbl">A sentence that sounds exactly like Tayst</div>
        <input className="f-inp" value={form.signature_phrase} placeholder='e.g. Good coffee is not an amenity.'
          onChange={e=>setForm(f=>({...f,signature_phrase:e.target.value}))} />
      </div>
      <div className="f-row">
        <div className="f-lbl">Tone</div>
        <div className="tone-grid">
          {TONE_OPTIONS.map(o=>(
            <button key={o.id} className={`tone-btn${form.tones.includes(o.id)?' on':''}`} onClick={()=>toggleTone(o.id)}>{o.label}</button>
          ))}
        </div>
      </div>
      <div className="f-row">
        <div className="f-lbl">Words we never use</div>
        <div style={{ marginBottom:8 }}>
          {form.banned.length===0?<span style={{fontSize:11,color:'#9ca3af'}}>None added yet</span>:form.banned.map(w=>(
            <span key={w} style={{ display:'inline-flex',alignItems:'center',gap:4,background:'#fce8ef',color:'#7c2d4a',borderRadius:14,padding:'2px 9px',fontSize:11,fontWeight:600,margin:2 }}>
              {w}<span style={{cursor:'pointer'}} onClick={()=>removeBanned(w)}>✕</span>
            </span>
          ))}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <input className="f-inp" style={{flex:1}} placeholder='e.g. game-changer, synergy…' value={bannedInput}
            onChange={e=>setBannedInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addBanned()} />
          <button className="btn btn-outline btn-sm" onClick={addBanned}>Add</button>
        </div>
      </div>
      <div style={{ paddingTop:12, borderTop:'1px solid #e5e7eb' }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving?'Saving…':'💾 Save Voice Profile'}
        </button>
        {saved&&<span style={{marginLeft:10,color:'#2d5c3c',fontSize:12,fontWeight:600}}>✅ Saved!</span>}
      </div>
    </div>
  );
}

// ── Partners ──────────────────────────────────────────────────
function PartnersPanel() {
  const { partners, addPartner, removePartner } = useApp();
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({name:'',type:'hotel',linkedin:'',location:'',description:'',monitor:true});
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.name) return;
    setSaving(true);
    try {
      await addPartner({ ...form, initials:makeInitials(form.name), tags:[] });
      setForm({name:'',type:'hotel',linkedin:'',location:'',description:'',monitor:true});
      setShowAdd(false);
    } catch(err) { alert('Failed: '+err.message); }
    finally { setSaving(false); }
  }

  const TYPE_COLORS = { hotel:'var(--caramel)', partner:'var(--leaf)', prospect:'var(--sky-dk)' };

  return (
    <div>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
        <div><div className="set-title">Customers & Partners</div><div className="set-sub">Hotel and business partners for monitoring and partner briefs.</div></div>
        <button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(s=>!s)}>{showAdd?'✕ Cancel':'+ Add Company'}</button>
      </div>
      {showAdd && (
        <div style={{ background:'var(--fog)', border:'1px solid var(--border)', borderRadius:'var(--r)', padding:16, marginBottom:16 }}>
          <div className="f-g2">
            <div><div className="f-lbl">Company Name *</div><input className="f-inp" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Arlo Hotels" /></div>
            <div><div className="f-lbl">Type</div>
              <select className="f-sel" value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value}))}>
                <option value="hotel">Hotel / Client</option>
                <option value="partner">Certification / Partner</option>
                <option value="prospect">Prospect</option>
              </select>
            </div>
          </div>
          <div className="f-g2">
            <div><div className="f-lbl">LinkedIn URL</div><input className="f-inp" value={form.linkedin} onChange={e=>setForm(f=>({...f,linkedin:e.target.value}))} placeholder="https://linkedin.com/company/…" /></div>
            <div><div className="f-lbl">Location</div><input className="f-inp" value={form.location} onChange={e=>setForm(f=>({...f,location:e.target.value}))} placeholder="City, State" /></div>
          </div>
          <div className="f-row"><div className="f-lbl">Description</div><textarea className="f-ta" rows={2} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} /></div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <input type="checkbox" id="monCb" checked={form.monitor} onChange={e=>setForm(f=>({...f,monitor:e.target.checked}))} />
            <label htmlFor="monCb" style={{fontSize:12}}>Monitor on LinkedIn (include in Feed Scan)</label>
          </div>
          <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>{saving?'Adding…':'Add Company'}</button>
        </div>
      )}
      {partners.length===0?(
        <div className="empty-state"><div style={{fontSize:28,marginBottom:8}}>🏨</div>No partners added yet.</div>
      ):(
        <div style={{ display:'grid', gap:8 }}>
          {partners.map(p=>(
            <div key={p.id} style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:'12px 14px', boxShadow:'var(--sh)', display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:avatarColor(p.initials||p.name), display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'#fff', flexShrink:0 }}>{p.initials||'?'}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ fontSize:13, fontWeight:700 }}>{p.name}</div>
                  <span style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', padding:'2px 7px', borderRadius:5, background:(TYPE_COLORS[p.type]||'#9ca3af')+'20', color:TYPE_COLORS[p.type]||'#9ca3af' }}>{p.type}</span>
                  {p.monitor&&<span style={{fontSize:9,color:'#9ca3af'}}>👁 monitoring</span>}
                </div>
                {p.location&&<div style={{fontSize:11,color:'#9ca3af'}}>{p.location}</div>}
                {p.description&&<div style={{fontSize:11,color:'#6b7280',marginTop:2}}>{p.description.slice(0,100)}</div>}
              </div>
              <div style={{ display:'flex', gap:5 }}>
                {p.linkedin&&<a href={p.linkedin} target="_blank" rel="noreferrer" className="btn btn-outline btn-xs">↗ LinkedIn</a>}
                <button className="btn btn-ghost btn-xs" style={{color:'var(--rose)'}} onClick={()=>{if(confirm('Remove?'))removePartner(p.id).catch(console.error);}}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── API Keys ──────────────────────────────────────────────────
function SourceToggle({ value, onChange }) {
  const options = [
    { v: 'env',    label: '🖥  Server Key' },
    { v: 'custom', label: '🔑  My Own Key' },
  ];
  return (
    <div style={{ display:'inline-flex', background:'var(--fog)', borderRadius:8, padding:3, border:'1px solid var(--border)', marginBottom:14 }}>
      {options.map(({ v, label }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          style={{
            padding:'5px 16px',
            borderRadius:6,
            border:'none',
            fontSize:11.5,
            fontWeight:600,
            cursor:'pointer',
            background: value === v ? 'var(--white)' : 'transparent',
            color:       value === v ? 'var(--ink)'   : '#9ca3af',
            boxShadow:   value === v ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
            transition:  'all .15s',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function APIKeysPanel() {
  const { anthropicKey, openaiKey, setAnthropicKey, setOpenaiKey } = useApp();

  // Source: 'env' = backend handles AI calls using server .env key
  //         'custom' = user supplies their own key (stored in browser)
  const [anthSource, setAnthSourceState] = useState(
    () => localStorage.getItem('tayst_anth_source') || 'custom'
  );
  const [oaiSource, setOaiSourceState] = useState(
    () => localStorage.getItem('tayst_oai_source') || 'custom'
  );

  const [anthInput, setAnthInput] = useState(anthropicKey || '');
  const [oaiInput,  setOaiInput]  = useState(openaiKey || '');
  const [anthSaved, setAnthSaved] = useState(false);
  const [oaiSaved,  setOaiSaved]  = useState(false);

  function setAnthSource(src) {
    localStorage.setItem('tayst_anth_source', src);
    setAnthSourceState(src);
  }
  function setOaiSource(src) {
    localStorage.setItem('tayst_oai_source', src);
    setOaiSourceState(src);
  }

  function saveAnth() {
    const k = anthInput.trim(); if (!k) { alert('Paste your Anthropic key first.'); return; }
    setAnthropicKey(k); setAnthSaved(true); setTimeout(() => setAnthSaved(false), 2500);
  }
  function saveOai() {
    setOpenaiKey(oaiInput.trim()); setOaiSaved(true); setTimeout(() => setOaiSaved(false), 2500);
  }

  return (
    <div>
      <div className="set-title">API Keys</div>
      <div className="set-sub">Connect AI services to enable content generation and image creation.</div>

      {/* ── Anthropic ─────────────────────────────────────── */}
      <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:18, boxShadow:'var(--sh)', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'var(--caramel-ll)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🤖</div>
          <div>
            <div style={{fontSize:13,fontWeight:700}}>Anthropic API Key</div>
            <div style={{fontSize:10.5,color:'#9ca3af'}}>Required — powers all content generation</div>
          </div>
          <div style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color: anthSource==='env' || anthropicKey ? 'var(--leaf)' : 'var(--rose)' }}>
            {anthSource === 'env' ? '✓ Server Key' : anthropicKey ? '✓ Connected' : 'Not set'}
          </div>
        </div>

        <SourceToggle value={anthSource} onChange={setAnthSource} />

        {anthSource === 'custom' && (
          <>
            <div className="f-row">
              <div className="f-lbl">API Key</div>
              <input className="f-inp" type="password" style={{fontFamily:'monospace',fontSize:11}}
                placeholder="sk-ant-api03-…" value={anthInput} onChange={e => setAnthInput(e.target.value)} />
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={saveAnth}>Save Key</button>
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" style={{fontSize:11,color:'var(--sky-dk)'}}>Get key ↗</a>
            </div>
            {anthSaved && <div className="ok-bar" style={{marginTop:8}}>✅ Anthropic key saved.</div>}
          </>
        )}
      </div>

      {/* ── OpenAI ────────────────────────────────────────── */}
      <div style={{ background:'var(--white)', borderRadius:'var(--r)', border:'1px solid var(--border)', padding:18, boxShadow:'var(--sh)', marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'var(--mist)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}>🎨</div>
          <div>
            <div style={{fontSize:13,fontWeight:700}}>OpenAI API Key</div>
            <div style={{fontSize:10.5,color:'#9ca3af'}}>Optional — DALL-E 3 image generation</div>
          </div>
          <div style={{ marginLeft:'auto', fontSize:11, fontWeight:700, color: oaiSource==='env' || openaiKey ? 'var(--leaf)' : '#9ca3af' }}>
            {oaiSource === 'env' ? '✓ Server Key' : openaiKey ? '✓ Connected' : 'Not set'}
          </div>
        </div>

        <SourceToggle value={oaiSource} onChange={setOaiSource} />

        {oaiSource === 'custom' && (
          <>
            <div className="f-row">
              <div className="f-lbl">API Key</div>
              <input className="f-inp" type="password" style={{fontFamily:'monospace',fontSize:11}}
                placeholder="sk-…" value={oaiInput} onChange={e => setOaiInput(e.target.value)} />
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <button className="btn btn-primary btn-sm" onClick={saveOai}>Save Key</button>
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" style={{fontSize:11,color:'var(--sky-dk)'}}>Get key ↗</a>
            </div>
            {oaiSaved && <div className="ok-bar" style={{marginTop:8}}>✅ OpenAI key saved.</div>}
          </>
        )}
      </div>

      <div className="info-box amber">
        <span style={{fontSize:14,flexShrink:0}}>🔒</span>
        <div>
          <strong>Server Key</strong> — AI calls are handled by the server using the key in its .env file. Your key is never exposed to the browser.{' '}
          <strong>My Own Key</strong> — stored in your browser only and sent directly to the AI provider.
        </div>
      </div>
    </div>
  );
}

// ── Users (admin) ─────────────────────────────────────────────
function UsersPanel() {
  const { currentUser, appUsers, addUser, updateUser } = useApp();
  const isAdmin = currentUser?.role==='admin';
  const [form, setForm] = useState({name:'',email:'',role:'creator',password:''});
  const [err,  setErr]  = useState('');
  const [saving, setSaving] = useState(false);
  const ROLE_CSS = { admin:'rp-admin', creator:'rp-creator', contributor:'rp-contributor' };

  async function createUser() {
    setErr('');
    const {name,email,role,password} = form;
    if(!name||!email||!password){setErr('All fields are required.');return;}
    if(password.length<8){setErr('Password must be at least 8 characters.');return;}
    setSaving(true);
    try {
      await addUser({name,email,role,password,password_confirmation:password});
      setForm({name:'',email:'',role:'creator',password:''});
    } catch(err) {
      setErr(err.response?.data?.message||err.message||'Failed to create user.');
    } finally { setSaving(false); }
  }

  return (
    <div>
      <div className="sp-head">
        <div className="sp-title">Users & Access</div>
        <div className="sp-sub">{appUsers.filter(u=>u.active).length} active member{appUsers.filter(u=>u.active).length!==1?'s':''}</div>
      </div>
      <table className="users-table">
        <thead><tr><th>Member</th><th>Role</th><th>Status</th><th>Last Login</th>{isAdmin&&<th>Actions</th>}</tr></thead>
        <tbody>
          {appUsers.map(u=>(
            <tr key={u.id}>
              <td>
                <div style={{display:'flex',alignItems:'center',gap:9}}>
                  <div className="u-avatar" style={{background:avatarColor(u.avatar_initials||u.name)}}>{u.avatar_initials||makeInitials(u.name)}</div>
                  <div><div style={{fontWeight:600,fontSize:12.5}}>{u.name}</div><div style={{fontSize:10.5,color:'#9ca3af'}}>{u.email}</div></div>
                </div>
              </td>
              <td><span className={`role-pill ${ROLE_CSS[u.role]||''}`}>{u.role}</span></td>
              <td><span className={`status-pill ${u.active?'sp-active':'sp-inactive'}`}>{u.active?'● Active':'○ Inactive'}</span></td>
              <td style={{fontSize:11,color:'#9ca3af'}}>{u.last_login_at?new Date(u.last_login_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}):'Never'}</td>
              {isAdmin&&(
                <td>
                  <div style={{display:'flex',gap:5}}>
                    {u.active&&u.id!==currentUser?.id&&<button className="btn btn-outline btn-xs" style={{color:'var(--rose)',borderColor:'var(--rose)'}} onClick={()=>updateUser(u.id,{active:false}).catch(console.error)}>Deactivate</button>}
                    {!u.active&&<button className="btn btn-outline btn-xs" style={{color:'var(--leaf)',borderColor:'var(--leaf)'}} onClick={()=>updateUser(u.id,{active:true}).catch(console.error)}>Reactivate</button>}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {isAdmin&&(
        <div className="add-user-panel">
          <div className="aup-title">+ Add Team Member</div>
          <div className="f-g2">
            <div><div className="f-lbl">Full Name</div><input className="f-inp" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="e.g. Greg Smith" /></div>
            <div><div className="f-lbl">Email</div><input className="f-inp" type="email" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="greg@taystcoffee.com" /></div>
          </div>
          <div className="f-g2">
            <div><div className="f-lbl">Role</div>
              <select className="f-sel" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))}>
                <option value="contributor">Contributor — Ideas only</option>
                <option value="creator">Creator — Full content access</option>
                <option value="admin">Admin — Everything + settings</option>
              </select>
            </div>
            <div><div className="f-lbl">Temp Password</div><input className="f-inp" type="password" value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} placeholder="Min 8 chars" /></div>
          </div>
          {err&&<div className="error-bar" style={{marginBottom:8}}>{err}</div>}
          <div style={{display:'flex',gap:8,marginTop:4}}>
            <button className="btn btn-primary" onClick={createUser} disabled={saving}>{saving?'Creating…':'Create User'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Brand Settings ────────────────────────────────────────────
function BrandPanel() {
  const { settings, updateSettings } = useApp();
  const [form, setForm] = useState({
    companyName:     settings?.companyName||'Tayst Coffee',
    linkedinPage:    settings?.linkedinPage||'',
    businessClients: settings?.businessClients||200,
    hotelPartners:   settings?.hotelPartners||500,
    websiteUrl:      settings?.websiteUrl||'',
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  async function save() {
    setSaving(true);
    try { await updateSettings(form); setSaved(true); setTimeout(()=>setSaved(false),2500); }
    catch(err) { alert('Failed: '+err.message); }
    finally { setSaving(false); }
  }

  return (
    <div>
      <div className="set-title">Brand Settings</div>
      <div className="set-sub">Core information used across the studio.</div>
      <div style={{ maxWidth:420 }}>
        <div className="f-row"><div className="f-lbl">Company Name</div><input className="f-inp" value={form.companyName} onChange={e=>setForm(f=>({...f,companyName:e.target.value}))} /></div>
        <div className="f-row"><div className="f-lbl">LinkedIn Page</div><input className="f-inp" value={form.linkedinPage} onChange={e=>setForm(f=>({...f,linkedinPage:e.target.value}))} placeholder="https://linkedin.com/company/tayst-coffee" /></div>
        <div className="f-g2">
          <div><div className="f-lbl">Business Clients</div><input className="f-inp" type="number" value={form.businessClients} onChange={e=>setForm(f=>({...f,businessClients:e.target.value}))} /></div>
          <div><div className="f-lbl">Hotel Partners</div><input className="f-inp" type="number" value={form.hotelPartners} onChange={e=>setForm(f=>({...f,hotelPartners:e.target.value}))} /></div>
        </div>
        <div className="f-row"><div className="f-lbl">Website URL</div><input className="f-inp" value={form.websiteUrl} onChange={e=>setForm(f=>({...f,websiteUrl:e.target.value}))} placeholder="https://taystcoffee.com" /></div>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving…':'Save'}</button>
        {saved&&<div className="ok-bar" style={{marginTop:8}}>✅ Saved</div>}
      </div>
    </div>
  );
}

// ── Activity Log ──────────────────────────────────────────────
function formatDetail(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail.slice(0, 60);
  // Pick the most human-readable field in priority order
  const val = detail.name ?? detail.title ?? detail.status ?? detail.channel
           ?? detail.key  ?? detail.count ?? detail.role  ?? null;
  if (val !== null) return String(val).slice(0, 60);
  // Fallback: join all key=value pairs
  return Object.entries(detail)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ')
    .slice(0, 80);
}

function ActivityPanel() {
  const { activityLog } = useApp();
  return (
    <div>
      <div className="sp-head"><div className="sp-title">Activity Log</div><div className="sp-sub">Last 100 actions</div></div>
      {activityLog.length===0
        ? <div style={{fontSize:12,color:'#9ca3af',paddingTop:8}}>No activity recorded yet.</div>
        : activityLog.map((a,i)=>(
          <div key={i} className="act-item">
            <div className="act-avi" style={{background:avatarColor(a.initials||'?')}}>{a.initials||'?'}</div>
            <div className="act-text"><strong>{a.userName||'System'}</strong> {a.action}{a.detail&&<span style={{color:'#9ca3af'}}> · {formatDetail(a.detail)}</span>}</div>
            <div className="act-time">{new Date(a.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
          </div>
        ))
      }
    </div>
  );
}

// ── My Account ────────────────────────────────────────────────
function AccountPanel() {
  const { currentUser, logout } = useApp();
  const [form, setForm] = useState({current:'',next:'',confirm:''});
  const [err,  setErr]  = useState('');
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);

  if (!currentUser) return <div style={{color:'#9ca3af'}}>Not signed in.</div>;

  async function changePassword() {
    setErr('');
    if(!form.current||!form.next){setErr('All fields required.');return;}
    if(form.next.length<8){setErr('New password must be at least 8 characters.');return;}
    if(form.next!==form.confirm){setErr("Passwords don't match.");return;}
    setSaving(true);
    try {
      await authApi.changePassword(form.current, form.next);
      setSaved(true); setForm({current:'',next:'',confirm:''});
      setTimeout(()=>setSaved(false),2500);
    } catch(err) { setErr(err.response?.data?.message||err.message||'Failed.'); }
    finally { setSaving(false); }
  }

  const initials = currentUser.avatar_initials || makeInitials(currentUser.name);

  return (
    <div>
      <div className="set-title">My Account</div>
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:12, background:'var(--fog)', borderRadius:'var(--r)', marginBottom:18, border:'1px solid var(--border)' }}>
        <div style={{ width:38, height:38, borderRadius:'50%', background:avatarColor(initials), display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff' }}>{initials}</div>
        <div>
          <div style={{fontWeight:700}}>{currentUser.name}</div>
          <div style={{fontSize:11,color:'#9ca3af'}}>{currentUser.email}</div>
          <span style={{ fontSize:8.5, fontWeight:700, textTransform:'uppercase', padding:'2px 7px', borderRadius:5, background:currentUser.role==='admin'?'var(--gold-l)':'var(--mist)', color:currentUser.role==='admin'?'var(--gold)':'var(--leaf)' }}>{currentUser.role}</span>
        </div>
      </div>
      <div style={{ maxWidth:300 }}>
        <div style={{fontSize:13,fontWeight:700,marginBottom:11}}>Change Password</div>
        <div className="f-row"><div className="f-lbl">Current</div><input className="f-inp" type="password" value={form.current} onChange={e=>setForm(f=>({...f,current:e.target.value}))} /></div>
        <div className="f-row"><div className="f-lbl">New (min 8 chars)</div><input className="f-inp" type="password" value={form.next} onChange={e=>setForm(f=>({...f,next:e.target.value}))} /></div>
        <div className="f-row"><div className="f-lbl">Confirm</div><input className="f-inp" type="password" value={form.confirm} onChange={e=>setForm(f=>({...f,confirm:e.target.value}))} /></div>
        {err&&<div className="error-bar" style={{marginBottom:8}}>{err}</div>}
        {saved&&<div className="ok-bar" style={{marginBottom:8}}>✅ Password updated.</div>}
        <button className="btn btn-primary" onClick={changePassword} disabled={saving}>{saving?'Saving…':'Update Password'}</button>
        <div style={{marginTop:16,paddingTop:14,borderTop:'1px solid var(--border)'}}>
          <button className="btn btn-danger" onClick={logout}>Sign Out of Studio</button>
        </div>
      </div>
    </div>
  );
}
